import { useQuery } from '@tanstack/react-query';

import { getRequest } from '@/api/rest/hooks/requests/get';
import {
  GET_ORGANIZATION_SEARCH_PATH,
  GetOrganizationSearchErrorDto,
  GetOrganizationSearchSuccessDto,
} from '@/api/rest/resources/types/search';
import { assertIsDefined } from '@/utils/assert';
import { isNullish } from '@/utils/booleans';

import { useNCAParentOrganizationId } from './useNCAParentOrganizationId';
import { useOrganizationId } from './useOrganizationId';

type SuccessDto = GetOrganizationSearchSuccessDto;
type ErrorDto = GetOrganizationSearchErrorDto;

export const useOrganizationSearch = (organizationIdArg?: string, filters?: Record<string, unknown> | null) => {
  const orgId = useOrganizationId();
  const organizationId = organizationIdArg ?? orgId;
  const ncaParentId = useNCAParentOrganizationId();

  assertIsDefined(organizationId, 'organizationId');

  const pathVariables = { organization_id: organizationId };
  const queryParams = { ...filters, ...(ncaParentId ? { nca_parent_id: ncaParentId } : {}) };

  const query = filters?.query as string | undefined;
  const enabled = !isNullish(query) && query?.trim().length > 0;

  const queryFn = () => {
    return getRequest<SuccessDto>(GET_ORGANIZATION_SEARCH_PATH, pathVariables, queryParams);
  };

  return useQuery<SuccessDto, ErrorDto>({
    queryKey: ['organization', pathVariables, 'search', queryParams],
    queryFn,
    enabled,
    staleTime: 1000 * 30,
  });
};
